import React, { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from '@src/helpers/toaster'
import { isAxiosError } from 'axios'

import { useGetMyAccount } from '@queries/Account'
import { useAcceptInvite, useGetInviteData } from '@queries/Teams'
import routes from '@src/Routes/routes'
import { requestErrorsHandler } from '../utils'

const Auth = (Cmp: React.ComponentType) => {
  const Authenticated = (props: React.ComponentProps<typeof Cmp>) => {
    const { data: user, isInitialLoading, isSuccess: isLoggedIn, refetch: getAccount } = useGetMyAccount()
    const { mutateAsync: acceptInvite } = useAcceptInvite()
    const onboarded = !!user?.team?.onboarded
    const navigate = useNavigate()

    const location = useLocation()
    const { search, state, pathname } = location

    const query = new URLSearchParams(search)
    const invite = query.get('invite') as string

    const { isFetched: isInviteFetched, error: inviteCodeError } = useGetInviteData()

    const handleInviteCheck = async () => {
      try {
        if (isInviteFetched && !inviteCodeError) {
          await acceptInvite({ invite })
          navigate(pathname, { replace: true })
          toast.success('You successfully joined to the Team')
          await getAccount()
        } else {
          if (inviteCodeError?.response?.status === 404) {
            toast.error('Invite code does not exist')
          } else {
            requestErrorsHandler(inviteCodeError)
          }
          navigate(pathname)
        }
      } catch (err) {
        if (isAxiosError(err) && err?.response?.status !== 500) {
          requestErrorsHandler(err)
        }
      }
    }

    useEffect(() => {
      if (!!user && invite) {
        handleInviteCheck()
      }
    }, [user, isInviteFetched, inviteCodeError])

    if (isLoggedIn) {
      if (!onboarded && !pathname.startsWith('/onboarding')) {
        return (
          <Navigate
            to={{
              pathname: '/onboarding',
            }}
            state={{ ...(state || {}), prev: pathname }}
          />
        )
      }

      return <Cmp {...props} />
    }

    if (!isLoggedIn && !isInitialLoading) {
      return (
        <Navigate
          to={{
            pathname: invite ? routes.public.signup : routes.public.login,
            search,
          }}
          state={{ ...(state || {}), prev: pathname }}
        />
      )
    }

    return null
  }

  return Authenticated
}

export default Auth
